import { useCallback, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * useDryRunApply — Sub-sprint D-α hook for the dry-run → apply contract.
 *
 * Contract:
 *   1. The consumer provides `buildPayload()` returning the canonical
 *      intent object (without `apply` / `confirmation_token`).
 *   2. `runDryRun()` POSTs `{...buildPayload(), apply: false}` and stores
 *      the response (including `confirmation_token` and
 *      `kill_switch_active`) under `dryRun`.
 *   3. The hook tracks the canonical payload signature at dry-run time.
 *      If `buildPayload()` produces a different signature on a later
 *      render, `stale=true` — apply is refused.
 *   4. `applyAction(extra)` POSTs `{...buildPayload(), ...extra,
 *      apply: true, confirmation_token: dryRun.confirmation_token}`.
 *      It refuses to fire if `dryRun` is null, stale, or
 *      `kill_switch_active=true`.
 *
 * The hook never exposes `confirmation_token` via URL or logs;
 * consumers must render the masked tail only (see `DryRunDiffPanel`).
 */
const _signature = (obj) => {
  try { return JSON.stringify(obj || null, Object.keys(obj || {}).sort()); }
  catch { return ""; }
};

export default function useDryRunApply({ endpoint, buildPayload }) {
  const [dryRun, setDryRun] = useState(null);
  const [dryRunSignature, setDryRunSignature] = useState("");
  const [applyState, setApplyState] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentPayload = useMemo(() => {
    try { return buildPayload?.() || {}; } catch { return {}; }
  }, [buildPayload]);

  const currentSignature = useMemo(
    () => _signature(currentPayload),
    [currentPayload],
  );

  const stale =
    Boolean(dryRun) && Boolean(dryRunSignature) && dryRunSignature !== currentSignature;

  const runDryRun = useCallback(async () => {
    setError(null);
    setApplyState(null);
    setLoading(true);
    try {
      const body = { ...currentPayload, apply: false };
      const r = await api.post(endpoint, body);
      setDryRun(r.data || {});
      setDryRunSignature(currentSignature);
    } catch (e) {
      setDryRun(null);
      setDryRunSignature("");
      setError({
        status: e?.response?.status,
        detail: e?.response?.data?.detail || e?.message || "unknown",
      });
    } finally {
      setLoading(false);
    }
  }, [endpoint, currentPayload, currentSignature]);

  const applyAction = useCallback(async (extra = {}) => {
    setError(null);
    if (!dryRun) {
      setError({ status: 0, detail: "errors.dry_run_required" });
      return null;
    }
    if (stale) {
      setError({ status: 0, detail: "errors.dry_run_stale" });
      return null;
    }
    if (dryRun.kill_switch_active === true) {
      setError({ status: 0, detail: "errors.kill_switch_active" });
      return null;
    }
    if (!dryRun.confirmation_token) {
      setError({ status: 0, detail: "errors.confirmation_token_missing" });
      return null;
    }
    setLoading(true);
    try {
      const body = {
        ...currentPayload,
        ...extra,
        apply: true,
        confirmation_token: dryRun.confirmation_token,
      };
      const r = await api.post(endpoint, body);
      setApplyState(r.data || {});
      return r.data || {};
    } catch (e) {
      setError({
        status: e?.response?.status,
        detail: e?.response?.data?.detail || e?.message || "unknown",
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [endpoint, dryRun, stale, currentPayload]);

  const reset = useCallback(() => {
    setDryRun(null);
    setDryRunSignature("");
    setApplyState(null);
    setError(null);
  }, []);

  return { dryRun, applyState, error, loading, stale, runDryRun, applyAction, reset };
}
